import { Component, EventEmitter, OnInit, Output, inject, input, signal } from '@angular/core';
import { Organization } from '../../../organization-models';
import { AccessGroup, AccessGroupSearchEvent, CreateAccessGroup } from '../../../../access-groups/access-group.model';
import { AccessGroupService } from '../../../../../shared/services/access-group.service';
import { map, Subject, switchMap, take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DiscountType } from '../../../../../shared/enums/discount-type.enum';
import { PageEvent } from '@angular/material/paginator';
import { SnackbarService } from 'ok-angular-shared/components';
import { EnvironmentLoaderService } from '../../../../../shared/services/environment-loader.service';
import { RequiresFeatureRoleDirective } from '../../../../../shared/directives/requires-feature-role.directive';
import { Step3AccessGroupsFormComponent } from '../step3-access-groups-form/step3-access-groups-form.component';
import { TranslateAccessGroupCarTypeEnum } from '../../../../../shared/pipes/translate-access-group-car-type-enum.pipe';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { AccessGroupCustomerNumbersModalComponent } from 'src/app/shared/components/access-group-customer-numbers-modal/access-group-customer-numbers-modal.component';

@Component({
  selector: 'adm-step3-access-groups',
  templateUrl: './step3-access-groups.component.html',
  styleUrl: './step3-access-groups.component.scss',
  imports: [
    RequiresFeatureRoleDirective,
    Step3AccessGroupsFormComponent,
    TranslateAccessGroupCarTypeEnum,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatBadgeModule,
  ],
})
export class Step3AccessGroupsComponent implements OnInit {
  private accessGroupService = inject(AccessGroupService);
  private snackbarService = inject(SnackbarService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private dialog = inject(MatDialog);
  private reloadAccessGroupsTrigger$ = new Subject<void>();

  organization = input.required<Organization>();
  priceRegulationsEnabled = input<boolean>(false);
  @Output() skipStep = new EventEmitter<void>();

  public accessGroups = signal<AccessGroup[]>([]);
  public accessGroupBeingEdited: AccessGroup | null = null;

  public displayColumns: string[] = ['name', 'amount', 'carType', 'actions'];
  public createStandardAccessGroupsButtonDisabled = false;
  public showForm = false;
  public roles = this.environmentLoaderService.virtualRoles;
  public reloadAccessGroups$ = this.reloadAccessGroupsTrigger$.pipe(
    switchMap(() => {
      const pageEvent = new PageEvent();
      pageEvent.pageSize = 10000;
      const searchEvent = new AccessGroupSearchEvent(pageEvent);
      searchEvent.organizationId = this.organization().id;

      return this.accessGroupService.getAccessGroups(searchEvent).pipe(map(result => result.items));
    }),
    tap(items => this.accessGroups.set(items))
  );

  protected readonly DiscountType = DiscountType;

  get discountType(): DiscountType {
    return this.organization().discountType as DiscountType;
  }

  get discountUnit(): string {
    switch (this.discountType) {
      case DiscountType.Percentages:
        return '%';
      case DiscountType.FixedAmount:
        return 'kr.';
    }
  }

  get hasCreatedStandardAccessGroups() {
    return (
      this.accessGroups().some((a: AccessGroup) => a.name === 'Medarbejdere') ||
      this.accessGroups().some((a: AccessGroup) => a.name === 'Firmabiler') ||
      this.accessGroups().some((a: AccessGroup) => a.name === 'Gæster')
    );
  }
  constructor() {
    this.reloadAccessGroups$.pipe(takeUntilDestroyed()).subscribe();
  }

  ngOnInit(): void {
    this.reloadAccessGroups();
  }

  public reloadAccessGroups() {
    this.reloadAccessGroupsTrigger$.next();
  }

  public createStandardAccessGroups() {
    this.createStandardAccessGroupsButtonDisabled = true;
    this.accessGroupService
      .assignStandardAccessGroups(this.organization().id)
      .pipe(take(1))
      .subscribe(() => {
        this.reloadAccessGroups();
      });
  }

  public handleSkipToNextStep() {
    this.skipStep.emit();
  }

  public editAccessGroup(accessGroup: AccessGroup) {
    this.accessGroupBeingEdited = accessGroup;
    this.showForm = true;
  }

  public limitAccessGroupAccess(accessGroup: AccessGroup) {
    const dialogRef = this.dialog.open(AccessGroupCustomerNumbersModalComponent, {
      data: {
        accessGroupIdentifier: accessGroup.identifier,
      },
      width: '720px',
    });

    dialogRef.afterClosed().subscribe(accessGroupUpdated => {
      if (accessGroupUpdated) {
        this.updateAccessGroup(accessGroupUpdated);
      }
    });
  }

  private updateAccessGroup(accessGroupUpdated: AccessGroup): void {
    this.accessGroups.update(accessGroups =>
      accessGroups.map(accessGroup =>
        accessGroup.identifier === accessGroupUpdated.identifier
          ? {
              ...accessGroup,
              ...accessGroupUpdated,
              accessCount: this.getUpdatedAccessCount(accessGroup, accessGroupUpdated),
            }
          : accessGroup
      )
    );
  }

  private getUpdatedAccessCount(accessGroup: AccessGroup, accessGroupUpdated: AccessGroup): number {
    const currentCustomerNumberCount = accessGroup.customerNumbers?.length ?? 0;
    const updatedCustomerNumberCount = accessGroupUpdated.customerNumbers?.length ?? 0;

    return accessGroup.accessCount - currentCustomerNumberCount + updatedCustomerNumberCount;
  }

  public deleteAccessGroup(accessGroup: AccessGroup) {
    this.accessGroupService
      .deleteAccessGroup(accessGroup.identifier)
      .pipe(take(1))
      .subscribe(() => {
        this.reloadAccessGroups();
      });
  }

  public handleSave(accessGroup: Partial<CreateAccessGroup>) {
    if (!accessGroup.name || !accessGroup.carType) {
      this.snackbarService.error('Adgangsgruppe mangler obligatoriske felter.');
      return;
    }

    const request: CreateAccessGroup = {
      name: accessGroup.name,
      amount: accessGroup.amount,
      identifier: accessGroup.identifier,
      organizationId: this.organization().id,
      connectorIdentifiers: accessGroup.connectorIdentifiers ?? [],
      carType: accessGroup.carType,
      sharedDiscountIdentifier: accessGroup.sharedDiscountIdentifier,
    };
    this.accessGroupService
      .saveAccessGroup(request)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.showForm = false;
          this.reloadAccessGroups();
          this.accessGroupBeingEdited = null;
        },
        error: err => {
          const errorCode = (err as { error?: { code?: number } })?.error?.code;

          if (errorCode === 13100) {
            this.snackbarService.error('Adgangsgruppe med dette navn findes allerede i denne organisation.');
          } else {
            this.snackbarService.error(`Der skete en ukendt fejl under ${this.accessGroupBeingEdited ? 'redigering' : 'oprettelse'}`);
          }
        },
      });
  }

  public handleShowForm() {
    this.showForm = true;
  }

  public handleCancelForm() {
    this.showForm = false;
    this.accessGroupBeingEdited = null;
  }
}
